import {DateRange} from '@blueprintjs/datetime2';
import {ChartData} from 'chart.js';
import React from 'react';

import {PieGroup} from 'app/components/Dashboard/GenericChart/PieGroupControls';
import {BaseLensStatsWidget} from 'app/components/Dashboard/OrgLensStats/BaseLensStatsWidget';
import {
  getLensStatsPercentLoaded,
  makePieChartCountData,
} from 'app/components/Dashboard/utils/chartUtils';

import {useOrgNoteStats} from '../hooks/useOrgNoteStats';
import {NotesQueryParams} from '../types';

/**
 * Widget that displays a pie chart of property counts by portfolio or tag
 */
const VALID_NOTE_GROUPINGS: PieGroup[] = [
  {
    label: 'Portfolio',
    id: 'portfolio',
    singleMembership: true,
    groupingField: 'projectId',
    labelField: 'projectName',
  },
  {
    label: 'Note Tag',
    id: 'noteTag',
    singleMembership: false,
    groupingField: 'tags',
    labelField: 'tags',
  },
  {
    label: 'Author',
    id: 'author',
    singleMembership: true,
    groupingField: 'author',
    labelField: 'author',
  },
];

export const OrgNotesWidget: React.FC<{orgId: string}> = ({orgId}) => {
  const today = new Date();
  const lastYear = new Date(today);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const [selectedPieGroup, setSelectedPieGroup] = React.useState<PieGroup>(VALID_NOTE_GROUPINGS[0]);

  //TODO(eva): store this in local storage so we don't have to refetch if the user changes it, navs away, and returns??
  const [noteParams, setNoteParams] = React.useState<NotesQueryParams>({
    noteType: 'user',
    startDate: lastYear.toISOString(),
    endDate: today.toISOString(),
  });

  const {
    data: notesData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = useOrgNoteStats(orgId, noteParams);

  const percentLoaded = getLensStatsPercentLoaded(notesData);

  const chartData: ChartData<'pie'> = React.useMemo(() => {
    if (!notesData) {
      return {labels: [], datasets: []} as ChartData<'pie'>;
    }

    return makePieChartCountData(notesData, selectedPieGroup);
  }, [notesData, selectedPieGroup]);

  // Continuously fetch data page by page so we can watch our data stream in
  // This is important for orgs with hundreds of features
  React.useEffect(() => {
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  return (
    <BaseLensStatsWidget
      error={error}
      widgetConfig={{
        title: `Note Counts by ${selectedPieGroup.label}`,
        type: 'chart',
        id: -2,
        isPublished: true,
      }}
      loading={!!(isFetching || isFetchingNextPage)}
      showControls={true}
      showDateRangeInput={true}
      dateRange={[
        noteParams.startDate ? new Date(noteParams.startDate) : null,
        noteParams.endDate ? new Date(noteParams.endDate) : null,
      ]}
      setDateRange={(dateRange: DateRange) =>
        setNoteParams({
          ...noteParams,
          startDate: dateRange[0]?.toISOString() ?? undefined,
          endDate: dateRange[1]?.toISOString() ?? undefined,
        })
      }
      pieGroups={VALID_NOTE_GROUPINGS}
      selectedPieGroup={selectedPieGroup}
      setSelectedPieGroup={setSelectedPieGroup}
      chartData={chartData}
      plugins={[]}
      countItemLabel="notes"
      percentLoaded={percentLoaded}
    />
  );
};
